import React, { Children, Fragment } from 'react';
import { motion } from 'framer-motion';
import { ClipboardCheck, BarChart3, Play, Heart, ArrowRight, ArrowDown } from 'lucide-react';
interface PilotSlide2BProps {
  isDark?: boolean;
}
export function PilotSlide2B({
  isDark = false
}: PilotSlide2BProps) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  const pipelineSteps = [{
    id: 1,
    category: 'BASELINE',
    title: 'Culture Check™',
    description: 'One-time organizational assessment to establish leadership health baseline',
    icon: ClipboardCheck,
    bgColor: isDark ? 'bg-emerald-900/20' : 'bg-[#cbf0e0]',
    borderColor: isDark ? 'border-emerald-500/30' : 'border-emerald-100',
    iconColor: isDark ? 'text-emerald-300' : 'text-emerald-900'
  }, {
    id: 2,
    category: 'ASSESSMENT',
    title: 'Helium Score™',
    description: 'Quarterly psychometric assessment measuring leadership strain and resilience',
    icon: BarChart3,
    bgColor: isDark ? 'bg-amber-900/20' : 'bg-[#f5e6d3]',
    borderColor: isDark ? 'border-amber-500/30' : 'border-amber-100',
    iconColor: isDark ? 'text-amber-300' : 'text-amber-900'
  }, {
    id: 3,
    category: 'DEVELOPMENT',
    title: 'Helium Stream™',
    description: 'Auto-curated micro-learning content tailored to assessment results',
    icon: Play,
    bgColor: isDark ? 'bg-sky-900/20' : 'bg-[#cedfe9]',
    borderColor: isDark ? 'border-sky-500/30' : 'border-sky-100',
    iconColor: isDark ? 'text-sky-300' : 'text-sky-900'
  }, {
    id: 4,
    category: 'CARE NAVIGATION',
    title: 'Helium Care™',
    description: 'Assessment-driven matching to licensed mental health professionals',
    icon: Heart,
    bgColor: isDark ? 'bg-orange-900/20' : 'bg-[#fcebd9]',
    borderColor: isDark ? 'border-orange-500/30' : 'border-orange-100',
    iconColor: isDark ? 'text-orange-300' : 'text-orange-900'
  }];
  return <motion.div className="h-full w-full overflow-y-auto overflow-x-hidden" variants={containerVariants} initial="hidden" animate="visible" data-id="element-582">
      <div className="min-h-full flex flex-col justify-center max-w-7xl mx-auto px-6 sm:px-12 py-16 sm:py-20" data-id="element-583">
        {/* Header Section */}
        <motion.div variants={itemVariants} className="text-center mb-12 sm:mb-16" data-id="element-584">
          <p className={`text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-585">
            What Heliona Offers
          </p>
          <h1 className={`text-3xl sm:text-5xl md:text-6xl font-serif font-medium tracking-tight mb-6 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-586">
            An integrated leadership wellness pipeline
          </h1>
        </motion.div>

        {/* Pipeline Cards */}
        <div className="relative" data-id="element-587">
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-6 lg:gap-0" data-id="element-588">
            {pipelineSteps.map((step, index) => <Fragment key={step.id} data-id="element-589">
                <motion.div variants={itemVariants} className={`relative flex flex-col p-6 sm:p-8 rounded-3xl lg:flex-1 ${step.bgColor} ${isDark ? 'border ' + step.borderColor : ''} transition-transform hover:scale-[1.02] duration-300`} data-id="element-590">
                  <div className="mb-6" data-id="element-591">
                    <step.icon className={`w-8 h-8 sm:w-10 sm:h-10 ${step.iconColor} stroke-[1.5]`} data-id="element-592" />
                  </div>

                  <div className="flex-1" data-id="element-593">
                    <p className={`text-xs font-semibold tracking-wider uppercase mb-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-594">
                      {step.category}
                    </p>
                    <h3 className={`text-xl sm:text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-595">
                      {step.title}
                    </h3>
                    <p className={`text-sm sm:text-base leading-relaxed ${isDark ? 'text-white/70' : 'text-gray-600'}`} data-id="element-596">
                      {step.description}
                    </p>
                  </div>

                  {/* Mobile Arrow (Down) */}
                  {index < pipelineSteps.length - 1 && <div className="lg:hidden absolute -bottom-6 left-1/2 transform -translate-x-1/2 z-10" data-id="element-597">
                      <ArrowDown className={`w-6 h-6 ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-598" />
                    </div>}
                </motion.div>

                {/* Desktop Arrow (Right) */}
                {index < pipelineSteps.length - 1 && <motion.div variants={itemVariants} className="hidden lg:flex items-center justify-center px-2 shrink-0 z-10" data-id="element-599">
                    <ArrowRight className={`w-6 h-6 ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-600" />
                  </motion.div>}
              </Fragment>)}
          </div>
        </div>

        {/* Footer Tagline */}
        <motion.div variants={itemVariants} className="mt-16 text-center" data-id="element-601">
          <p className={`text-sm sm:text-lg max-w-3xl mx-auto leading-relaxed ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-602">
            Each product informs the next—creating a continuous feedback loop
            for measurable outcomes.
          </p>
        </motion.div>
      </div>
    </motion.div>;
}